import React, { useEffect, useState } from "react"
import { Container, Row, Col, Button, Card, Form, Tabs, Tab, Badge } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useParams } from "react-router-dom"
import { useAuth } from "../components/auth/useAuth"
import { editReceipt, getPaymentMethods, getReceipts, readReceipt } from "../actions/other/receiptsActions"
import { getSucursalSettings } from "../actions/other/sucursalsActions"
import { RootState, AppDispatch } from "../store"
import { CustomDataTable } from "../components/design/Datatables/CustomDataTable"
import { Modal } from "react-bootstrap"
import Loader from "../components/loaders/Loader"
import { get } from "react-hook-form"
import ReceiptForm from "../components/forms/ReceiptForm"
import Swal from "sweetalert2"
import { ArrowLeft, MoveRight } from "lucide-react"


export interface Receipt {
    _id?: string
    id?: string
    idCliente?: string
    idPoliza?: string
    tipo?: string
    clase?: string
    nifCliente?: string
    ordinal?: string
    numero?: string
    fechaEfecto?: string
    estado?: string
    primaTotal?: string
    comisionCorreduria?: string
    nombreCompletoTomador?: string
    medioCobro?: string
}

export default function ReceiptDetail() {
    const { t } = useTranslation()
    const dispatch: AppDispatch = useDispatch()
    const { id } = useParams()
    const location = useLocation()
    const { clientauth } = useAuth()

    const { receipt, total, loadingdata, resultreceipt, paymentMethods } = useSelector((state: RootState) => state.receipts)
    const { settings: sucursalSettings } = useSelector((state: RootState) => state.sucursals)

    const [isEditing, setIsEditing] = useState(false)
    const [receiptSelected, setReceiptSelected] = useState<Receipt | null>(null)

    const [optionsPaymentMethods, setOptionsPaymentMethods] = useState<{ value: string, label: string }[]>([])

    useEffect(() => {
        if (clientauth) {
            dispatch(getPaymentMethods())
            if (clientauth.rol === 'commercial' && clientauth.idSucursal) {
                dispatch(getSucursalSettings(clientauth.idSucursal) as any)
            }
        }
    }, [dispatch, t, clientauth])

    useEffect(() => {
        if (receipt) {
            setReceiptSelected(receipt)
        }
    }, [receipt])

    useEffect(() => {
        if (id) {
            dispatch(readReceipt(id) as any)
        }
    }, [id, dispatch])

    const handleInputChange = (e: any) => {
        const { name, value } = e.target
        setReceiptSelected(prev => (prev ? { ...prev, [name]: value } : null))
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsEditing(true)
        dispatch(editReceipt(receiptSelected, receipt?._id) as any)
    }

    useEffect(() => {
        if (!resultreceipt) return

        dispatch({ type: "RECEIPT_RESET_RESULT" })
        if (resultreceipt === "editsuccess") {
            // Recargar para obtener _id/ebrokerId asignados por el backend.
            // Sin esto la nota sigue pareciendo editable y se puede reenviar a ebroker.
            if (id) {
                dispatch(readReceipt(id) as any)
            }
            Swal.fire({
                icon: "success",
                confirmButtonText: t("global.great"),
                text: t("receiptTranslate.editSuccess"),
            }).then(() => {
                setIsEditing(false)
            })
        }
    }, [resultreceipt, dispatch, t, id])

    const pathParts = location.pathname.split("/")
    const base = pathParts[1]

    const getBadgePropsByStatus = (status: string) => {
        if (!status) return { bg: "secondary" };

        if (status === "PROCESANDO_DATOS") {
            return { bg: "secondary" }; // gris
        }

        if (status.startsWith("ANULADO")) {
            return { bg: "danger" }; // rojo
        }

        if (status.startsWith("COBRADO")) {
            return { bg: "primary" }; // azul
        }

        if (status.startsWith("PENDIENTE_DEVUELTO")) {
            return {
                style: {
                    backgroundColor: "#fd7e14",
                    color: "white",
                },
            }; // naranja
        }

        if (status.startsWith("PENDIENTE")) {
            return { bg: "success" }; // verde
        }

        return { bg: "secondary" };
    };

    return (
        <Container fluid className="p-1 py-5 p-md-5">
            <Row>
                <Col>
                    <h1>{t("menus.receipts")}</h1>
                </Col>
            </Row>
            <Row className="my-3">
                <Col>
                    <Tabs
                        defaultActiveKey="general"
                        id="client-tabs"
                        className="mb-0"
                        fill
                    >
                        <Tab eventKey="general" title={t('receiptTranslate.receipt')}>
                            <div className="border border-top-0 rounded-bottom p-4 bg-white">
                                {loadingdata
                                    ?
                                    <Loader />
                                    :
                                    <Row className="my-1">
                                        <Col xs={12} className="mb-3 d-flex align-items-center justify-content-between">
                                            <Badge {...getBadgePropsByStatus(receipt?.descripcionEstado)}>{receipt?.descripcionEstado}</Badge>
                                            <div className="d-flex align-items-center gap-3">
                                                <Link to={`/${base}/clientes/${receipt?.idCliente}`} className="btn btn-secondary">
                                                    {t("sinesterTranslate.viewClient")} <MoveRight size={16} />
                                                </Link>
                                                <Link to={`/${base}/polizas/${receipt?.idPoliza}`} className="btn btn-secondary">
                                                    {t("sinesterTranslate.viewPolicy")} <MoveRight size={16} />
                                                </Link>
                                            </div>
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>
                                                {t("receiptTranslate.ordinal")}
                                            </h5>
                                            <hr />
                                            {receipt?.ordinal || "—"}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.numero")}</h5>
                                            <hr />
                                            {receipt?.numero || "—"}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3"></Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.importeTotal")}</h5>
                                            <hr />
                                            {receipt?.primaTotal
                                                ? (clientauth?.rol === 'commercial' && !sucursalSettings.showHSP
                                                    ? receipt?.primaTotal + " €"
                                                    : (receipt?.primaTotal + receipt?.hsp) + " €")
                                                : "—"}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.fechaEfecto")}</h5>
                                            <hr />
                                            {(() => {
                                                if (!receipt?.fechaEfecto) return "—";
                                                const raw = receipt.fechaEfecto;
                                                // Intentamos arreglar años con más de 4 dígitos
                                                const fixed = raw.replace(/^(\d{4})\d+/, "$1");
                                                const date = new Date(fixed);
                                                if (isNaN(date.getTime())) return "—";
                                                return new Intl.DateTimeFormat('es-ES', {
                                                    year: 'numeric',
                                                    month: '2-digit',
                                                    day: '2-digit',
                                                    timeZone: 'Europe/Madrid'
                                                }).format(date);
                                            })()}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.periodicidad")}</h5>
                                            <hr />
                                            {receipt?.formaPago || "—"}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3"></Col>
                                        {(clientauth?.rol !== 'commercial' || sucursalSettings.showHSP) && (
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.HSP")}</h5>
                                            <hr />
                                            {receipt?.hsp || "—"}
                                        </Col>
                                        )}
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.fechaDevolucion")}</h5>
                                            <hr />
                                            {receipt?.fechaDevolucion && new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Madrid' })?.format(new Date(receipt?.fechaDevolucion || "—"))}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.fechaAnulacion")}</h5>
                                            <hr />
                                            {receipt?.fechaAnulacion && new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Madrid' })?.format(new Date(receipt?.fechaAnulacion || "—"))}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3"></Col>
                                        {(clientauth?.rol !== 'commercial' || sucursalSettings.showComisiones) && (
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.comisionCorreduria")}</h5>
                                            <hr />
                                            {receipt?.comisionCorreduria + " €" || "—"}
                                        </Col>
                                        )}
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.paymentDate")}</h5>
                                            <hr />
                                            {receipt?.fechaCobro && new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Europe/Madrid' })?.format(new Date(receipt?.fechaCobro || "—"))}
                                        </Col>
                                        <Col xs={12} lg={3} className="mb-3">
                                            <h5>{t("receiptTranslate.paymentMethod")}</h5>
                                            <hr />
                                            {receipt?.paymentMethod || "—"}
                                        </Col>
                                        <Col xs={12} lg={6} className="mb-3">
                                            <h5>{t("receiptTranslate.cuentaBancaria")}</h5>
                                            <hr />
                                            {(receipt?.arrayBanks ?? []).join(", ") || "—"}
                                        </Col>
                                    </Row>
                                }
                            </div>
                        </Tab>
                        <Tab eventKey="comentarios" title={t('policyTranslate.comments')}>
                            <div className="border border-top-0 rounded-bottom px-1 py-2 bg-white">
                                <ReceiptForm
                                    state={receiptSelected}
                                    handleInputChange={handleInputChange}
                                    submit={handleSubmit}
                                    isEditing={isEditing}
                                    loadingdata={loadingdata}
                                />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Container>
    )
}